import { AnalyticsEventNameType } from "@coeff/api";
import { message } from "antd";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  useArchiveWebDashboardMutation,
  useDuplicateWebDashboardMutation,
  useRenameWebDashboardMutation,
} from "../../api";
import {
  ArchiveIcon,
  DuplicateIcon,
  EllipsisMenu,
  PencilIcon,
  ThemePaletteIcon,
} from "../../components";
import { useTrack } from "../../utils";
import { DuplicateDashboardModal } from "../Dashboard/components/DuplicateDashboardModal";
import { RenameDashboardModal } from "../Dashboard/components/RenameDashboardModal";

import type { ThemeBackground, ThemeMode } from "./themes";
import { ThemeSelector } from "./ThemeSelector";
import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

interface NavBarMoreMenuProps {
  isOwner: boolean;
  isOpen: boolean;
  canEditDashboard: boolean;
  /** Any authenticated viewer may make their own copy (same-data duplicate). */
  canDuplicate: boolean;
  /** Hides the Archive action when the dashboard is already archived (restore lives in the banner). */
  isArchived?: boolean;
  /** Disables the menu while the dashboard is building, like the other nav actions. */
  isDashboardBuilding?: boolean;
  dropdownRef: React.RefObject<HTMLDivElement>;
  buttonRef: React.RefObject<HTMLButtonElement>;
  onThemesClick: () => void;
  onRenameClick: () => void;
  onArchiveClick: () => void;
  /** Closes the dropdown when the Duplicate modal opens. */
  onDuplicateClick: () => void;
  /** Fired after a successful archive so the banner can show the first-time Undo/Delete state. */
  onArchived?: () => void;
  onNavButtonClick: () => void;
  // ThemeSelector props
  isEditingEnabled: boolean;
  themeId: string;
  /** Selector mode toggle (light/dark). */
  mode: ThemeMode;
  /** Selector background toggle (gradient on/off). */
  background: ThemeBackground;
  handleThemeChange: (next: { themeId?: string; mode?: string; background?: string }) => void;
  targetDocument?: Document;
  isThemeSelectorOpen: boolean;
  setIsThemeSelectorOpen: (isOpen: boolean) => void;
  /** Live-preview a theme change locally without persisting (selector clicks). */
  onPreviewTheme?: (next: { themeId?: string; mode?: string; background?: string }) => void;
  /** Selector closed with changes: parent shows the "Theme updated" toast with a Reset action. */
  onThemeCommitted?: (revertTo: { themeId: string; mode: string; background: string }) => void;
  onThemeUncommitted?: () => void;
  /** Custom theme deleted: parent shows a toast whose Undo restores it. */
  onThemeDeleted?: (deleted: { themeId: string; name: string }) => void;
  onThemeDeleteFailed?: () => void;
  /** "+ Custom" / "Request custom theme": parent opens the chat with the theme request card. */
  onRequestTheme?: () => void;
  /** Edit a custom theme: parent opens the editor chat prefilled with the theme context. */
  onEditTheme?: (edit: { themeId: string; colors: string[] }) => void;
}

export const NavBarMoreMenu: React.FC<NavBarMoreMenuProps> = ({
  isOwner,
  canEditDashboard,
  canDuplicate,
  isOpen,
  isArchived = false,
  isDashboardBuilding = false,
  dropdownRef,
  onThemesClick,
  onRenameClick,
  onArchiveClick,
  onDuplicateClick,
  onArchived,
  onNavButtonClick,
  themeId,
  mode,
  background,
  handleThemeChange,
  targetDocument,
  isThemeSelectorOpen,
  setIsThemeSelectorOpen,
  onPreviewTheme,
  onThemeCommitted,
  onThemeUncommitted,
  onThemeDeleted,
  onThemeDeleteFailed,
  onRequestTheme,
  onEditTheme,
  isEditingEnabled,
  buttonRef,
}) => {
  const { dashboardId, dashboardName, spreadsheetId } = useWebDashboardViewerContext();
  const history = useHistory();
  const track = useTrack();
  const renameMutation = useRenameWebDashboardMutation();
  const archiveMutation = useArchiveWebDashboardMutation();
  const duplicateMutation = useDuplicateWebDashboardMutation();

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const openRenameModal = () => {
    setRenameValue(dashboardName ?? "");
    setIsRenameModalOpen(true);
    onRenameClick();
  };

  const openDuplicateModal = () => {
    setIsDuplicateModalOpen(true);
    onDuplicateClick();
  };

  // Same-data copy: shares the source's spreadsheet + tab versions, so the new dashboard renders
  // immediately with no rebuild. On success we navigate straight to the copy. On failure we let
  // the error propagate so the modal shows the reason inline (no easy-to-miss top toast).
  const handleDuplicateSameData = async (title: string) => {
    const result = await duplicateMutation.mutateAsync({ web_dashboard_id: dashboardId, title });
    setIsDuplicateModalOpen(false);
    message.success("Dashboard duplicated");
    history.push(`/dashboards/${result.web_dashboard_id}`);
  };

  // Archive is a direct action (no confirmation modal) — the archived banner appears once the
  // permissions/dashboard caches invalidate. Restoring is handled by that banner, not this menu.
  const handleArchive = async () => {
    onArchiveClick();
    try {
      await archiveMutation.mutateAsync({ web_dashboard_id: dashboardId, archived: true });
      message.success("Dashboard archived");
      onArchived?.();
    } catch {
      message.error("Failed to archive dashboard");
    }
  };

  const handleRename = async () => {
    const newTitle = renameValue.trim();
    if (!newTitle) {
      return;
    }

    try {
      await renameMutation.mutateAsync({
        web_dashboard_id: dashboardId,
        title: newTitle,
      });
      track(AnalyticsEventNameType.VibeDashListDashboardRenamed, {
        vibe_dash_id: dashboardId,
        vibe_dash_name: newTitle,
        ss_id: spreadsheetId,
      });
      message.success("Dashboard renamed");
      setIsRenameModalOpen(false);
    } catch {
      // Leave the modal open so the user can retry; the toast surfaces the failure.
      message.error("Failed to rename dashboard");
    }
  };

  if (!canEditDashboard && !canDuplicate) {
    // Nothing to show: viewers who cannot even duplicate (e.g. anonymous public-link viewers)
    // get no kabob menu. Editors always have actions; authenticated viewers get Duplicate.
    return null;
  }

  return (
    <div className="relative">
      <button
        type="button"
        className={`themed-nav-button px-3 ${
          isDashboardBuilding ? "opacity-60 cursor-not-allowed" : ""
        }`}
        onClick={onNavButtonClick}
        disabled={isDashboardBuilding}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title={
          isDashboardBuilding
            ? "More options are available after the dashboard finishes building"
            : "More options"
        }
        ref={buttonRef}
      >
        <EllipsisMenu transform="rotate(90)" fill="currentColor" />
      </button>

      {isOpen && !isDashboardBuilding && (
        <div
          ref={dropdownRef}
          className="themed-dropdown-menu absolute right-0 top-full mt-2"
          role="menu"
        >
          <div className="flex flex-col items-start rounded-xl w-full">
            {canEditDashboard && (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="themed-dropdown-item flex gap-1.5 h-6 w-full h-auto items-center cursor-pointer text-left"
                  onClick={() => {
                    onThemesClick();
                  }}
                >
                  <span className="size-6 shrink-0 flex items-center justify-center">
                    <ThemePaletteIcon fill="currentColor" width="16" height="16" />
                  </span>
                  <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                    Theme
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="themed-dropdown-item flex gap-1.5 h-6 w-full h-auto items-center cursor-pointer text-left"
                  onClick={openRenameModal}
                >
                  <span className="size-6 shrink-0 flex items-center justify-center">
                    <PencilIcon fill="currentColor" />
                  </span>
                  <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                    Rename
                  </span>
                </button>
              </>
            )}
            {canDuplicate && (
              <button
                type="button"
                role="menuitem"
                className="themed-dropdown-item flex gap-1.5 h-6 w-full h-auto items-center cursor-pointer text-left"
                onClick={openDuplicateModal}
              >
                <span className="size-6 shrink-0 flex items-center justify-center">
                  <DuplicateIcon width="16" height="16" />
                </span>
                <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                  Duplicate
                </span>
              </button>
            )}
            {!isArchived && isOwner && (
              <button
                type="button"
                role="menuitem"
                className="themed-dropdown-item flex gap-1.5 h-6 w-full h-auto items-center cursor-pointer text-left"
                onClick={handleArchive}
              >
                <span className="size-6 shrink-0 flex items-center justify-center">
                  <ArchiveIcon fill="currentColor" />
                </span>
                <span className="text-sm text-main-foreground leading-[18px] whitespace-nowrap">
                  Archive
                </span>
              </button>
            )}
          </div>
        </div>
      )}
      {isEditingEnabled && !isOpen && isThemeSelectorOpen && (
        <ThemeSelector
          themeId={themeId}
          mode={mode}
          background={background}
          handleThemeChange={handleThemeChange}
          targetDocument={targetDocument}
          isOpen={isThemeSelectorOpen}
          setIsOpen={setIsThemeSelectorOpen}
          onRequestTheme={() => onRequestTheme?.()}
          onPreviewTheme={onPreviewTheme}
          onThemeCommitted={onThemeCommitted}
          onThemeUncommitted={onThemeUncommitted}
          onThemeDeleted={onThemeDeleted}
          onThemeDeleteFailed={onThemeDeleteFailed}
          onEditTheme={onEditTheme}
        />
      )}

      <RenameDashboardModal
        visible={isRenameModalOpen}
        renameValue={renameValue}
        isLoading={renameMutation.isLoading}
        onRenameValueChange={setRenameValue}
        onConfirm={handleRename}
        onCancel={() => setIsRenameModalOpen(false)}
      />

      <DuplicateDashboardModal
        visible={isDuplicateModalOpen}
        sourceName={dashboardName ?? ""}
        sourceUrl={`${window.location.origin}/dashboards/${dashboardId}`}
        onDuplicateSameData={handleDuplicateSameData}
        onCancel={() => setIsDuplicateModalOpen(false)}
      />
    </div>
  );
};
